import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button } from '@heroui/react';
import { Sheet } from '@heroui-pro/react';
import type { LocaleCode } from '@/api/models';
import { isoToDisplayDate } from '@/pages/accounting/statements/statementMeta';
import { cn } from '@/utils/cnUtils';
import { formatGel } from '@/utils/money';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { ACTIVITY_STATUS_CHIP } from '../../activityMeta';
import { ReverseDialog } from '../reverseDialog';
import { useActivityDetailDrawer } from './useActivityDetailDrawer';

type ActivityDetailDrawerProps = {
    /** The record to show; `null` closes the drawer. */
    recordId: string | null;
    locale: LocaleCode;
    /** Whether this session may reverse. The server decides again either way. */
    canReverse: boolean;
    onClose: () => void;
};

const Field: React.FC<{ label: string; value: string; slot: string }> = ({
    label,
    value,
    slot,
}) => (
    <div className="flex flex-col gap-1">
        <span className="text-xs font-medium uppercase tracking-wide text-muted">
            {label}
        </span>
        <span
            className="text-sm font-semibold text-foreground"
            {...getNodeTestIdProp(`operations-activity-detail-${slot}`)}
        >
            {value}
        </span>
    </div>
);

/**
 * One recorded operation, in full.
 *
 * A right-side `Sheet`, the sibling of the payroll-run and fixed-asset drawers,
 * and self-served for the same reason: the lines it shows are the **entry's**,
 * which the register's rows do not carry.
 *
 * Four things it exists to answer, in the order a reader asks them:
 *
 * - **what posted** — the journal lines with their codes and names, under totals
 *   that prove the entry balances;
 * - **who, when, and under which recipe** — the provenance trio. The version is
 *   the load-bearing one: it is frozen, so "why did this land in those accounts?"
 *   has an answer years later even after the template has been rewritten twice;
 * - **what was asked** — the slot answers, which the entry itself cannot carry
 *   because the posting engine merged lines that shared an account;
 * - **whether it still stands** — and if not, the reversal that cancelled it,
 *   with its reason, its author and a link both ways.
 *
 * A failed read renders **here** rather than throwing. Every page in this folder
 * throws to its boundary; a drawer is the one place that is wrong, because
 * throwing would replace a working register with a full-page error over one row
 * that would not open.
 */
export const ActivityDetailDrawer: React.FC<ActivityDetailDrawerProps> = ({
    recordId,
    locale,
    canReverse,
    onClose,
}) => {
    const { t } = useTranslation('pages');
    const [isConfirmOpen, setIsConfirmOpen] = React.useState(false);
    const drawer = useActivityDetailDrawer({ recordId, locale });
    const { detail } = drawer;
    const row = detail?.row ?? null;
    const chip = row ? ACTIVITY_STATUS_CHIP[row.status] : null;

    // A reversal that succeeded closes the dialog: the drawer behind it has
    // refetched and now shows the Reversed state, which is the confirmation.
    React.useEffect(() => {
        if (row?.status === 'REVERSED') {
            setIsConfirmOpen(false);
        }
    }, [row?.status]);

    return (
        <Sheet
            isOpen={recordId !== null}
            onOpenChange={(open) => !open && onClose()}
            placement="right"
        >
            <Sheet.Backdrop>
                <Sheet.Content className="w-full sm:max-w-[680px]">
                    <Sheet.Dialog
                        aria-label={t('operations.activity.detail.title')}
                        {...getNodeTestIdProp('operations-activity-drawer')}
                    >
                        <Sheet.CloseTrigger />
                        <Sheet.Header>
                            <Sheet.Heading>
                                <span className="flex flex-col gap-1">
                                    <span
                                        className="text-lg font-semibold"
                                        {...getNodeTestIdProp(
                                            'operations-activity-detail-name',
                                        )}
                                    >
                                        {row
                                            ? locale === 'ka'
                                                ? row.nameKa
                                                : row.nameEn
                                            : t(
                                                  'operations.activity.detail.title',
                                              )}
                                    </span>
                                    <span className="text-sm font-normal text-muted">
                                        {t(
                                            'operations.activity.detail.subtitle',
                                        )}
                                    </span>
                                </span>
                            </Sheet.Heading>
                        </Sheet.Header>

                        <Sheet.Body className="flex flex-col gap-5">
                            {drawer.hasError ? (
                                <p
                                    className="rounded-xl bg-danger-soft px-4 py-3 text-sm text-danger-soft-foreground"
                                    {...getNodeTestIdProp(
                                        'operations-activity-detail-error',
                                    )}
                                >
                                    {t('operations.activity.detail.error')}
                                </p>
                            ) : drawer.isLoading || !detail || !row || !chip ? (
                                <p
                                    className="text-sm text-muted"
                                    {...getNodeTestIdProp(
                                        'operations-activity-detail-loading',
                                    )}
                                >
                                    {t('operations.activity.detail.loading')}
                                </p>
                            ) : (
                                <>
                                    <div className="flex flex-wrap items-center gap-3">
                                        <span
                                            className={cn(
                                                'inline-flex w-fit items-center rounded-lg px-2.5 py-1 text-xs font-medium',
                                                chip.className,
                                            )}
                                            {...getNodeTestIdProp(
                                                'operations-activity-detail-status',
                                            )}
                                        >
                                            {t(chip.labelKey)}
                                        </span>
                                        <Button
                                            onPress={() =>
                                                drawer.onOpenEntry(
                                                    row.entryId,
                                                    row.date,
                                                )
                                            }
                                            size="sm"
                                            variant="outline"
                                            {...getNodeTestIdProp(
                                                'operations-activity-detail-open-entry',
                                            )}
                                        >
                                            <Icon
                                                aria-hidden
                                                icon="gravity-ui:arrow-up-right-from-square"
                                            />
                                            {row.entryNumberFormatted
                                                ? t(
                                                      'operations.activity.detail.openEntryNumbered',
                                                      {
                                                          number: row.entryNumberFormatted,
                                                      },
                                                  )
                                                : t(
                                                      'operations.activity.detail.openEntry',
                                                  )}
                                        </Button>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                                        <Field
                                            label={t(
                                                'operations.activity.detail.actor',
                                            )}
                                            slot="actor"
                                            value={row.recordedByName ?? '—'}
                                        />
                                        <Field
                                            label={t(
                                                'operations.activity.detail.date',
                                            )}
                                            slot="date"
                                            value={isoToDisplayDate(row.date)}
                                        />
                                        <Field
                                            label={t(
                                                'operations.activity.detail.template',
                                            )}
                                            slot="template"
                                            value={`${
                                                locale === 'ka'
                                                    ? row.nameKa
                                                    : row.nameEn
                                            } v${row.version}`}
                                        />
                                    </div>

                                    <table className="w-full text-sm">
                                        <caption className="sr-only">
                                            {t(
                                                'operations.activity.detail.caption',
                                            )}
                                        </caption>
                                        <thead>
                                            <tr className="border-b border-separator text-xs uppercase tracking-wide text-muted">
                                                <th
                                                    className="py-1 text-start font-medium"
                                                    scope="col"
                                                >
                                                    {t(
                                                        'operations.activity.detail.account',
                                                    )}
                                                </th>
                                                <th
                                                    className="py-1 text-end font-medium"
                                                    scope="col"
                                                >
                                                    {t(
                                                        'operations.activity.detail.debit',
                                                    )}
                                                </th>
                                                <th
                                                    className="py-1 text-end font-medium"
                                                    scope="col"
                                                >
                                                    {t(
                                                        'operations.activity.detail.credit',
                                                    )}
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {detail.lines.map((line) => (
                                                <tr
                                                    className="border-b border-separator/60"
                                                    key={`${line.accountId}-${line.debitTetri}-${line.creditTetri}`}
                                                    {...getNodeTestIdProp(
                                                        `operations-activity-detail-line-${line.accountId}`,
                                                    )}
                                                >
                                                    <th
                                                        className="py-1.5 text-start font-normal text-foreground"
                                                        scope="row"
                                                    >
                                                        {`${line.code} — ${line.name}`}
                                                    </th>
                                                    <td className="py-1.5 text-end tabular-nums">
                                                        {line.debitTetri > 0
                                                            ? formatGel(
                                                                  line.debitTetri,
                                                              )
                                                            : null}
                                                    </td>
                                                    <td className="py-1.5 text-end tabular-nums">
                                                        {line.creditTetri > 0
                                                            ? formatGel(
                                                                  line.creditTetri,
                                                              )
                                                            : null}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot>
                                            <tr
                                                className="font-medium"
                                                {...getNodeTestIdProp(
                                                    'operations-activity-detail-totals',
                                                )}
                                            >
                                                <th
                                                    className="py-2 text-start"
                                                    scope="row"
                                                >
                                                    {t(
                                                        'operations.activity.detail.total',
                                                    )}
                                                </th>
                                                <td className="py-2 text-end tabular-nums">
                                                    {formatGel(
                                                        detail.totalDebitTetri,
                                                    )}
                                                </td>
                                                <td className="py-2 text-end tabular-nums">
                                                    {formatGel(
                                                        detail.totalCreditTetri,
                                                    )}
                                                </td>
                                            </tr>
                                        </tfoot>
                                    </table>

                                    {detail.slots.length > 0 ? (
                                        <div
                                            className="flex flex-col gap-2"
                                            {...getNodeTestIdProp(
                                                'operations-activity-detail-slots',
                                            )}
                                        >
                                            <span className="text-xs font-medium uppercase tracking-wide text-muted">
                                                {t(
                                                    'operations.activity.detail.slots',
                                                )}
                                            </span>
                                            {detail.slots.map((slot) => (
                                                <span
                                                    className="flex flex-wrap items-baseline gap-2 text-sm"
                                                    key={slot.lineId}
                                                >
                                                    <span className="text-muted">
                                                        {(locale === 'ka'
                                                            ? slot.labelKa
                                                            : slot.labelEn) ||
                                                            slot.labelEn ||
                                                            slot.labelKa}
                                                    </span>
                                                    <span className="text-foreground">
                                                        {`${slot.code} — ${slot.name}`}
                                                    </span>
                                                </span>
                                            ))}
                                        </div>
                                    ) : null}

                                    {row.reversal ? (
                                        <div
                                            className="flex flex-col gap-2 rounded-2xl border border-separator bg-surface-secondary p-4"
                                            {...getNodeTestIdProp(
                                                'operations-activity-detail-reversal',
                                            )}
                                        >
                                            <span className="text-sm font-medium text-foreground">
                                                {t(
                                                    'operations.activity.detail.reversedTitle',
                                                )}
                                            </span>
                                            <p className="text-sm text-muted">
                                                {t(
                                                    'operations.activity.detail.reversedBy',
                                                    {
                                                        who:
                                                            row.reversal
                                                                .byUserName ??
                                                            '—',
                                                        date: isoToDisplayDate(
                                                            row.reversal.date,
                                                        ),
                                                    },
                                                )}
                                            </p>
                                            <p
                                                className="text-sm text-foreground"
                                                {...getNodeTestIdProp(
                                                    'operations-activity-detail-reversal-reason',
                                                )}
                                            >
                                                {row.reversal.reason ??
                                                    t(
                                                        'operations.activity.detail.noReason',
                                                    )}
                                            </p>
                                            <Button
                                                className="w-fit"
                                                onPress={() =>
                                                    drawer.onOpenEntry(
                                                        row.reversal!.entryId,
                                                        row.reversal!.date,
                                                    )
                                                }
                                                size="sm"
                                                variant="outline"
                                                {...getNodeTestIdProp(
                                                    'operations-activity-detail-open-reversal',
                                                )}
                                            >
                                                {row.reversal
                                                    .entryNumberFormatted
                                                    ? t(
                                                          'operations.activity.detail.openEntryNumbered',
                                                          {
                                                              number: row
                                                                  .reversal
                                                                  .entryNumberFormatted,
                                                          },
                                                      )
                                                    : t(
                                                          'operations.activity.detail.openEntry',
                                                      )}
                                            </Button>
                                        </div>
                                    ) : null}

                                    {/* Offered on a reversed entry too: the
                                        server refuses a second reversal, and
                                        pre-hiding the action would make this
                                        drawer's momentary view of the world the
                                        rule instead of the ledger's. */}
                                    {canReverse ? (
                                        <Button
                                            className="w-fit"
                                            onPress={() =>
                                                setIsConfirmOpen(true)
                                            }
                                            variant="outline"
                                            {...getNodeTestIdProp(
                                                'operations-activity-detail-reverse',
                                            )}
                                        >
                                            <Icon
                                                aria-hidden
                                                icon="gravity-ui:arrow-rotate-left"
                                            />
                                            {t(
                                                'operations.activity.detail.reverse',
                                            )}
                                        </Button>
                                    ) : null}

                                    <ReverseDialog
                                        error={drawer.reverseError}
                                        isOpen={isConfirmOpen}
                                        isReversing={drawer.isReversing}
                                        lines={detail.lines}
                                        onConfirm={drawer.onReverse}
                                        onOpenChange={(open) => {
                                            setIsConfirmOpen(open);
                                            if (!open) {
                                                drawer.onDismissReverseError();
                                            }
                                        }}
                                    />
                                </>
                            )}
                        </Sheet.Body>
                    </Sheet.Dialog>
                </Sheet.Content>
            </Sheet.Backdrop>
        </Sheet>
    );
};
